import { createFileRoute } from "@tanstack/react-router";

import FaqSection from "@/components/marketing/FaqSection";
import FinalCta from "@/components/marketing/FinalCta";
import HowItWorks from "@/components/marketing/HowItWorks";
import { Accent } from "@/components/marketing/MarketingSection";
import { marketingHead } from "@/components/marketing/marketingHead";
import ProductHero, { ProductShot } from "@/components/marketing/ProductHero";
import RelatedProducts from "@/components/marketing/RelatedProducts";
import SellFanData from "@/components/marketing/sell/SellFanData";
import SellFormats from "@/components/marketing/sell/SellFormats";
import SellStrategy from "@/components/marketing/sell/SellStrategy";
import Footer from "@/components/nav/Footer";

const LEDE =
  "Sell singles, albums, and exclusive content with zero Lutely commission. Set your price and see every sale as it happens.";

export const Route = createFileRoute("/sell-music-online")({
  component: SellMusicOnlinePage,
  head: () =>
    marketingHead({
      title: "Sell Music Online Commission-Free | Lutely",
      description: LEDE,
      path: "/sell-music-online",
    }),
});

const FAQ = [
  {
    question: "Does Lutely take a commission?",
    answer:
      "No. Lutely does not take a percentage commission from artist sales.",
  },
  {
    question: "Can I sell singles and albums?",
    answer:
      "Yes. Artists can publish and sell both single-track and multi-track releases.",
  },
  {
    question: "Can fans pay more than the listed price?",
    answer:
      "Yes. Pay-what-you-want lets fans add extra support above the minimum price.",
  },
  {
    question: "What happens after a fan buys music?",
    answer:
      "The purchase is added to the fan's Lutely library for streaming and available downloads.",
  },
];

function SellMusicOnlinePage() {
  return (
    <main className="flex flex-col bg-white">
      <ProductHero
        title={
          <>
            Sell music <Accent>directly</Accent> to your fans.
          </>
        }
        lede={LEDE}
        cta="Start Selling"
        proof={["Commission-free sales", "Pay what you want"]}
        stage={
          <ProductShot
            src="/marketing/sell/release-storefront.webp"
            alt="Direct-to-fan Lutely release storefront"
            caption="Real Lutely product screen"
          />
        }
      />
      <HowItWorks
        title="Your music. Your fans. Your revenue."
        intro="Build a direct relationship around music fans can stream, download, and own without a monthly subscription."
        steps={[
          {
            title: "Build the release",
            body: "Choose the tracks, artwork, price, date, and content you want to include.",
          },
          {
            title: "Publish to your storefront",
            body: "Give fans a focused page where they can hear, understand, and buy the release.",
          },
          {
            title: "Connect with your buyers",
            body: "Track sales, see available buyer information, and receive your earnings.",
          },
        ]}
      />
      <SellStrategy />
      <SellFormats />
      <SellFanData />
      <FaqSection items={FAQ} />
      <RelatedProducts current="sell" />
      <FinalCta
        title="You make the music. Lutely handles what comes next."
        body="Prepare your tracks, build the release, and sell directly to fans from one connected platform."
      />
      <Footer topClassName="bg-[#050923]" />
    </main>
  );
}
